<template>
  <section :class="$style.section">
    <div class="layout-container">
      <div
        :class="[$style.card, { [$style.is24]: !isHg, [$style.cardHg]: isHg }]"
        :style="isHg ? { backgroundImage: `url(${bgdTableHg})` } : undefined"
        data-test="table-view-renovation-planner"
      >
        <HgBadge v-bind="isHg ? { theme: 'info' } : {}" :class="$style.badge">
          {{ t('myProperties.landing.renovationPlanner.badge') }}
        </HgBadge>

        <h2 :class="$style.title">{{ t('myProperties.landing.renovationPlanner.title') }}</h2>

        <p :class="$style.description">
          {{ t('myProperties.landing.renovationPlanner.description') }}
        </p>

        <HgButton size="large" theme="secondary" :class="$style.cta" @click="goToOverview">
          {{ t('myProperties.landing.renovationPlanner.cta') }}
        </HgButton>

        <div :class="$style.imageWrapper">
          <img
            :src="phoneMockupImage"
            :alt="t('myProperties.landing.renovationPlanner.title')"
            :class="$style.image"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import bgdTableHg from '@/assets/images/landing-renovation-planner/bgd-table-hg.png?url';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { isPlatformHg } from '@/helpers/Platform/Platform';
import { getPropertyLandingImageUrl } from '@/helpers/Url/Url';
import { OVERVIEW_PAGE_PATH_SOURCE_LANDING } from '@/router/routes/constants';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { HgBadge, HgButton, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const isHg = isPlatformHg();
const { locale, t } = useI18n();
const router = useRouter();
const { trackClickEventWithTreatment } = useTrackingWithTreatment();

const renovationPlannerImagesHg: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-table-hg-de.png',
  ),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-table-hg-en.png',
  ),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-table-hg-fr.png',
  ),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-table-hg-it.png',
  ),
};

const renovationPlannerImagesIs24: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-table-is24-de.png',
  ),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-table-is24-en.png',
  ),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-table-is24-fr.png',
  ),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl(
    'renovation-planner/renovation-planner-table-is24-it.png',
  ),
};

const phoneMockupImage = computed(() => {
  const imagesByLocale = isHg ? renovationPlannerImagesHg : renovationPlannerImagesIs24;
  const currentLocale = locale.value as HgSupportedLanguage;

  return imagesByLocale[currentLocale] ?? imagesByLocale[HgSupportedLanguage.EN];
});

const goToOverview = () => {
  trackClickEventWithTreatment(
    TRACKING_ACTION.GO_TO_MY_REAL_ESTATE,
    TRACKING_LABEL.RENOVATION_PLANNER_TEASER,
  );

  router.push(OVERVIEW_PAGE_PATH_SOURCE_LANDING[locale.value]);
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.section {
  padding: $space-100 0;
  background-color: $color-white;
}

.card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: $space-200;
  background-color: $color-violet-0;
  border-radius: $border-radius-xl;
  padding: $space-300 $space-200;
  position: relative;

  &.is24 {
    background-color: $color-teal-50;

    .badge {
      background-color: #0d645c;
      border-color: #0d645c;
      color: #ffffff;
    }
  }

  &.cardHg {
    background-color: $color-violet-0;
    background-repeat: no-repeat;
    background-position: center bottom;
    background-size: 100% auto;
  }

  @include grid-media-query(sm) {
    padding: $space-300;
  }
}

.badge {
  display: inline-flex;
}

.title {
  @include heading-1;
  margin: 0;
  color: $color-black;
  max-width: 800px;
}

.description {
  @include body-2;
  margin: 0;
  color: $color-black;
  max-width: 800px;
}

.cta {
  width: fit-content;
  color: $color-blue-60;
}

.imageWrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}

.image {
  width: 100%;
  max-width: 800px;
  height: auto;
}
</style>
